import { useState, useEffect, useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import type { BiddingInfoResult, GetItemsFilters } from '../types';
import { BiddingItemStatusConst, CaltivationCategoryOptions, ProductionHistoryConst } from '../constants';
import { type ReportType, ReportConst } from '../constants/ReportConst';
import { TEA_CATEGORY } from '../constants/TeaCategoryConst';

// ==========================================
// 1. 純粋なデータ変換・判定ロジック（フックの外）
// ==========================================

/**
 * アイテムがフィルタ条件に合致するか判定
 * ※入札日(biddingDate)や出品区分(prefectureType)による絞り込みは、
 * 原則としてAPI（サーバー側）で行われるため、ここでは帳票表示直前の最終的な検証を行う
 */
const isTargetItem = (
  item: BiddingInfoResult,
  currentReportType: ReportType,
  fFrom: number | null,
  fTo: number | null
): boolean => {
  
  if (!item) return false;

  // ① 入札NOの範囲指定
  // ユーザーがフォームに入力した範囲に基づき、フロントエンド側で動的にフィルタリングを行う
  const itemNo = Number(item.biddingNumber);
  const matchNo = (fFrom === null || (!isNaN(itemNo) && itemNo >= fFrom)) &&
                  (fTo === null || (!isNaN(itemNo) && itemNo <= fTo));

  if (!matchNo) return false;

  // --- ステータス判定ロジック ---
  // 取得したデータの中から、帳票出力が許可されているステータスのもののみを抽出
  
  // 1. 全レポートタイプ共通：CONFIRMEDは常にOK
  if (item.status === BiddingItemStatusConst.CONFIRMED) {
    // OK
  } 
  // 2. 出品一覧表・手合票の場合のみ：APPLIEDもOK
  else if (
    (currentReportType === ReportConst.SYUKKO_ICHIRAN || currentReportType === ReportConst.TEAWASE) &&
    item.status === BiddingItemStatusConst.APPLIED
  ) {
    // OK
  } 
  // 3. それ以外はすべて除外
  else {
    return false;
  }

  return true;
};

/**
 * サーバーデータを帳票用DTOに変換
 */
const convertToPrintDTO = (item: BiddingInfoResult, cultivationMap: Map<string | number, string>, isKennai: boolean) => {
  const bDate = dayjs(item.biddingDate);
  const cultivationName = cultivationMap.get(item.caltivationCategory) || '';

  /**
   * 同額札の判定ロジック
   * 全入札データの中から、最高落札額と同じ額のデータ件数をカウント
   */
  // const samePriceCount = item.biddingInfoDatas?.filter(
  //   d => d.bidPrice === item.highestBidPrice
  // ).length || 0;
  // バックエンドから届いた resultMsg に「(抽)」という文字が入っているかどうかで判断する
  const isLotteryLabel = item.resultMsg?.includes("(抽)") ? "(抽)" : "";

  const cap1 = Number(item.capacity1) || 0;
  const qty1 = Number(item.quantity1) || 0;
  const cap2 = Number(item.capacity2) || 0;
  const qty2 = Number(item.quantity2) || 0;
  // 両方0の場合は②は表示しない
  const isSecondDataEmpty = cap2 === 0 && qty2 === 0;

  return {
    BidYotei: item.biddingDate,
    IsKennai: isKennai,
    HasHistory: item.productionHistory === ProductionHistoryConst.YES,
    IsShiageCha: item.teaCategory === TEA_CATEGORY.SHIAGECHA,
    BidMonth: bDate.isValid() ? bDate.month() + 1 : null,
    BidDate: bDate.isValid() ? bDate.date() : null,
    BidNo: Number(item.biddingNumber),
    Capacity1: cap1,
    Quantity1: qty1,
    Capacity2: isSecondDataEmpty ? null : cap2,
    Quantity2: isSecondDataEmpty ? null : qty2,
    BidMinPrice: Number(item.bidMinPrice) || 0,
    GapCode: item.gapCode,
    GapName: item.gapName,
    VarietyCode: item.varietyCode,
    VarietyName: item.varietyName,
    TeaTypeCode: item.teaTypeCode,
    TeaTypeName: item.teaTypeName,
    TeaSeasonCode: item.teaSeasonCode,
    TeaSeasonName: item.teaSeasonName,
    CaltivationCategoryCode: item.caltivationCategory,
    CaltivationCategoryName: cultivationName,
    SellerCode: item.sellerCode,
    SellerName: item.sellerName,
    FarmerCode: item.farmerCode,
    FarmerName: item.farmerName,
    Brand: item.brand,
    Amount: Math.round(((cap1 * qty1) + (cap2 * qty2)) * 10) / 10,
    BuyerName: item.highestBidBuyerName,
    HighestBidPrice: item.highestBidPrice,
    IsLottery: isLotteryLabel,
    IsFarmerHidden: item.isFarmerHidden,
    Biko: item.biko,
  };
};

// ==========================================
// 2. カスタムフック本体
// ==========================================

/**
 * 帳票出力に関するビジネスロジック（フィルタ・選択管理・C#通信）を管理するカスタムフック
 */
export const useReportLogic = (
  currentReportType: ReportType
) => {
  // 印刷処理（C#側のプレビュー表示・物理印刷）の実行中フラグ
  const [isPrinting, setIsPrinting] = useState(false);

  // --- 共通終了処理 ---
  const finalizePrinting = useCallback(() => {
    setIsPrinting(false);
    document.body.style.cursor = 'default';
  }, []);

  // --- 1. C# (WebView2) 側からのメッセージリスナー ---
  useEffect(() => {
    const handleMessageFromCSharp = (event: any) => {
      // 成功メッセージを受け取ったら印刷中フラグを下ろす
      if (event.data?.IsSuccess === true || event.data?.Type === "Success") {
        finalizePrinting();
      }
    };

    const webview = window.chrome?.webview;
    if (webview) {
      webview.addEventListener('message', handleMessageFromCSharp);
      return () => webview.removeEventListener('message', handleMessageFromCSharp);
    }
  }, [finalizePrinting]);

  // --- 2. マスタデータのMap化（名称変換用） ---
  const cultivationMap = useMemo(() => {
    return new Map<number | string, string>(
      CaltivationCategoryOptions.map(opt => [opt.value, opt.label])
    );
  }, []);

  // --- 3. 印刷実行ハンドラ (外部公開用) ---
  /**
   * フォームから受け取った条件で印刷を開始
   * データが未取得の場合は待機状態に移行し、APIレスポンスを待つ
   */
  const handleExecutePrint = useCallback(async (filters: GetItemsFilters, items: BiddingInfoResult[]) => {
    // すでに処理中の場合はガード
    if (isPrinting) return;

    setIsPrinting(true);
    document.body.style.cursor = 'wait';

    try {
      // そもそもデータが無い場合は即終了
      if (!items || items.length === 0) {
        alert("該当データなし");
        return finalizePrinting();
      }

      const fFrom = filters.biddingNumberFrom ? Number(filters.biddingNumberFrom) : null;
      const fTo = filters.biddingNumberTo ? Number(filters.biddingNumberTo) : null;

      const targets = items
        .filter(item => isTargetItem(item, currentReportType, fFrom, fTo))
        .sort((a, b) => (Number(a.biddingNumber) || 0) - (Number(b.biddingNumber) || 0));

      if (targets.length === 0) {
        alert("該当データなし");
        return finalizePrinting();
      }

      const currentPrefType = filters.prefectureType || 1;
      const isKennaiParam = Number(currentPrefType) === 1;

      const printData = targets.map(item => convertToPrintDTO(item, cultivationMap, isKennaiParam));

      if (window.chrome?.webview) {
        window.chrome.webview.postMessage({
          PageName: "Report",
          Function: "ExecutePrint",
          Payload: {
            ReportData: printData,
            TargetReportType: currentReportType
          }
        });
      } else {
        console.warn("WebView2 environment not found.");
        finalizePrinting();
      }
    } catch (err) {
      console.error("Print Error:", err);
      alert("印刷データの送信中にエラーが発生しました。");
      finalizePrinting();
    }
  }, [isPrinting, currentReportType, cultivationMap, finalizePrinting]);

  return {
    handleExecutePrint,
    // 実際の印刷通信中、またはデータ到着待ちの状態を「印刷中」として外部へ返す
    isPrinting: isPrinting
  };
};